import Vue from 'vue';
import type { State } from '@mimir/web/store/state';
import isEmpty from 'lodash/isEmpty';
import type { PersonResource } from '@mimir/web/pages/person/person-interfaces';
import type { StorageType } from '@mimir/common/dto/storageModel';
import type { SearchResultHit, LoadedItem } from '@mimir/common/dto/searchResultHit';
import type { StandardItemEntryDto } from '@mimir/common/dto/itemModels';
import { ItemState } from '@mimir/common/dto/itemState';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { LiveSourceDtoV2 } from '@mimir/common/dto/liveSourceDtos';
import type { RouterDto } from '@mimir/common/dto/routerControllerDtos';
import type { OptionListDto } from '@mimir/common/dto/optionListDto';
import { isMogrt, isPdfType } from '@mimir/common/util/mediaContentType';
import { updateCurrentItemMutation } from './timeline-module-mutations';
import type { ProgressInfo } from '@mimir/common/dto/progress';
import { isCurrentlyLive } from '@mimir/common/item/media';
import cloneDeep from 'lodash/cloneDeep';
import type { QuickPreview } from '../model/quickPreview';
import { searchState } from '@mimir/web/globals/searchState';
import { premiere } from '@mimir/web/globals/premiere';

export function patchItemDtosMutation(
  state: State,
  payload: {
    items: StandardItemEntryDto[],
    retrievedFields: (keyof LoadedItem)[],
    quickPreview: QuickPreview

  }
) {
  // TODO: Avoid O(n²)
  for (const itemDto of payload.items) {

    // Update the ongoing upload state
    for (const ongoingUpload of state.ongoingUploads) {
      if (ongoingUpload.id === itemDto.id) {
        ongoingUpload.updateFromItem(itemDto);
        break;
      }
    }

    // Update any hits in the search
    for (const hit of searchState.reactive.itemSearch.results.hits) {
      if (hit.id === itemDto.id) {
        Vue.set(hit, 'itemState', itemDto.itemState);
        Vue.set(hit, 'transcodingState', itemDto.transcodingState);
        Vue.set(hit, 'originalFileName', itemDto.originalFileName);
        Vue.set(hit, 'progressInfo', itemDto.progressInfo);
        Vue.set(hit, 'uploadProgressInfo', itemDto.uploadProgressInfo);

        Vue.set(hit, 'hasEditProxy', itemDto.hasEditProxy);
        Vue.set(hit, 'hasProxy', itemDto.hasProxy);
        Vue.set(hit, 'editProxyIngested', itemDto.editProxyIngested);
        Vue.set(hit, 'editProxyAvailable', itemDto.editProxyAvailable);
        Vue.set(hit, 'editProxyFormat', itemDto.editProxyFormat);
        Vue.set(hit, 'media', itemDto.media);
        Vue.set(hit, 'isLive', isCurrentlyLive(itemDto));
        Vue.set(hit, 'liveRecording', itemDto.liveRecording);
        Vue.set(hit, 'ingest', itemDto.ingest);
        Vue.set(hit, 'ingestSourceFullPath', itemDto.ingestSourceFullPath);
        Vue.set(hit, 'technicalMetadata', itemDto.technicalMetadata);
        Vue.set(hit, 'customIcons', itemDto.customIcons);

        if(itemDto.modifiedOn) {
          Vue.set(hit, 'modifiedOn', itemDto.modifiedOn);
        }
        if (hit.media) {
          // For live item we should keep polling until the resource is deleted.
          if (itemDto?.liveRecording && itemDto?.liveRecording.state !== 'resources_deleted') continue;

          if (hit.media.streaming === false && hit.itemState === ItemState.COMPLETE) {
            const index = state.itemIdsToPoll.indexOf(hit.id);
            if (index >= 0) {
              state.itemIdsToPoll.splice(index, 1);
            }
          }
        }
      }
    }

    // Update premiere clip items if they match
    if (premiere && premiere.clipInfos) {
      for (const key in premiere.clipInfos) {
        const clipInfo = premiere.clipInfos[key];
        if (clipInfo && clipInfo.mimirItemId === itemDto.id && clipInfo.mimirItem) {
          // Update transcoding state and proxy availability
          if (itemDto.transcodingState !== undefined) {
            Vue.set(clipInfo.mimirItem, 'transcodingState', itemDto.transcodingState);
          }
          if (itemDto.hasProxy !== undefined) {
            // Convert hasProxy boolean to proxy URL (null if not available)
            Vue.set(clipInfo.mimirItem, 'proxy', itemDto.hasProxy ? 'available' : null);
          }
          if (itemDto.itemState !== undefined) {
            Vue.set(clipInfo.mimirItem, 'itemState', itemDto.itemState);
          }
        }
      }
    }

    // Update the current item in the item page if present
    if (state.timeline.currentItem !== null && itemDto.id === state.timeline.currentItem.id) {

      /*
        TODO: BAD TYPE CAST (previously hidden by loose Vuex typing)
      */
      const update = itemDto as Partial<LoadedItem> & Pick<LoadedItem, 'id'>;

      updateCurrentItemMutation(state.timeline, { id: itemDto.id, update, retrievedFields: payload.retrievedFields });
    }

    const currentItem = payload.quickPreview.state.currentItem.value;

    // Also update the object that's currently being previewed in the QuickPreview pane
    if (currentItem !== null && itemDto.id === currentItem.id) {

      /*
        TODO: BAD TYPE CAST (previously hidden by loose Vuex typing)
      */
      const update = itemDto as Partial<LoadedItem> & Pick<LoadedItem, 'id'>;

      payload.quickPreview.ops.updateCurrentPreviewItemMutation({ id: itemDto.id, update, retrievedFields: payload.retrievedFields });
    }
  }
}

export const updatePersonInResultSet = (state: State, person: PersonResource) => {
  const results = searchState.reactive.itemSearch.results;
  const searchString = searchState.reactive.itemSearch.searchString;
  const index = results.hits.findIndex(obj => obj.id === person.id);
  if (index > -1) {
    const p = results.hits[index];
    p.name = person.name;
    p.description = person.description;
    if (p.thumbnail !== person.thumbnail && person.thumbnail) {
      p.thumbnail = person.thumbnail;
    }
  } else if (isEmpty(searchString) && !isEmpty(results.hits)) {
    const pseudoHit = <SearchResultHit>(<unknown>person);
    results.hits.unshift(pseudoHit);
  }
};

export const updateStorage = (state: State, updatedStorage: StorageType) => {
  const storage = state.storage.all.find((st: StorageType) => st.id === updatedStorage.id);
  if (storage) {
    storage.id = updatedStorage.id;
    storage.name = updatedStorage.name;
    storage.description = updatedStorage.description;
    storage.sidecarXmlKind = updatedStorage.sidecarXmlKind;
    storage.private = updatedStorage.private;
    storage.attachmentToTransfer = updatedStorage.attachmentToTransfer;
    storage.transferAttachments = updatedStorage.transferAttachments;
    storage.attachmentTransferRules = updatedStorage.attachmentTransferRules;
  }
};

export const updateLiveSource = (state: State, updated: LiveSourceDtoV2) => {
  const liveSource = state.liveSources.find(box => box.id === updated.id);
  if (liveSource) {
    liveSource.id = updated.id;
    liveSource.name = updated.name;
    liveSource.description = cloneDeep(updated.description);
    liveSource.locationId = updated.locationId;
    liveSource.expectedPipelines = updated.expectedPipelines;
    liveSource.defaultTargetFolderId = updated.defaultTargetFolderId;
  }
};


export const updateRouter = (state: State, updated: RouterDto) => {
  const router = state.routers.find(box => box.id === updated.id);
  if (router) {
    router.id = updated.id;
    router.displayName = updated.displayName;
    router.nameSize = updated.nameSize;
    router.controllers = [...updated.controllers];
    router.isNonExtendedRouter = updated.isNonExtendedRouter;
    router.sourceListQueryNotSupported = updated.sourceListQueryNotSupported;
    router.sourceList = updated.sourceList;
    router.matrixLevels = updated.matrixLevels;
  }
};


export const updateOptionList = (state: State, updated: OptionListDto) => {
  const list = state.optionLists.find(l => l.id === updated.id);
  if (list) {
    list.id = updated.id;
    list.name = updated.name;
    list.alternatives = updated.alternatives;
  }
};

function isMediaItem(itemDto: StandardItemEntryDto) {
  const isMedia = itemDto.itemType === ItemType.VIDEO ||
    itemDto.itemType === ItemType.AUDIO ||
    itemDto.itemType === ItemType.IMAGE ||
    (itemDto.itemType === ItemType.FILE && (isMogrt(itemDto.originalFileName) || isPdfType(itemDto.mediaType)));
  return isMedia;
}

function startedStreaming(before: StandardItemEntryDto | SearchResultHit, after: StandardItemEntryDto) {
  return before.media && !before.media.streaming && after.media?.streaming;
}

export function getOutdatedMediaItems(state: State, previousItems: StandardItemEntryDto[], latestItems: StandardItemEntryDto[]): StandardItemEntryDto[] {
  const hits = searchState.reactive.itemSearch.results.hits;
  const outdatedMediaItems: StandardItemEntryDto[] = [];
  for (const itemDto of latestItems) {
    const previousItem = previousItems.find((i) => i.id == itemDto.id) || hits.find((i) => i.id == itemDto.id);

    if (!previousItem
      || (isMediaItem(itemDto) && previousItem.transcodingState !== itemDto.transcodingState)
      || startedStreaming(previousItem, itemDto)
    ) {
      outdatedMediaItems.push(itemDto);
    }
  }
  return outdatedMediaItems;
}

/* Use this to quickly mark an item as being in a processing state while waiting for
 * the item poll to return the updated state after a modification in the backend */
export function patchItemProgressInfo(
  state: State,
  payload: { itemId: string, progressInfo: ProgressInfo, }
) {
  for (const hit of searchState.reactive.itemSearch.results.hits) {
    if (hit.id === payload.itemId) {
      Vue.set(hit, 'progressInfo', payload.progressInfo);
      break;
    }
  }
}
